/**
 * Feedback Survey Section Component
 * For tracking feedback on lost tenders
 */
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import { MessageSquare, Send, Loader2 } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { FEEDBACK_STATUS_OPTIONS } from './constants';

export function FeedbackSurveySection({ 
  tender, 
  canEdit, 
  isArchived, 
  feedbackData,
  setFeedbackData,
  onSave,
  onMarkSent,
  submitting 
}) {
  // Only show for Lost tenders
  if (tender.status !== 'Lost') {
    return null;
  }

  const handleSave = async () => {
    await onSave({
      status: feedbackData.status || tender.feedback_survey?.status || 'Not Sent',
      sent_date: feedbackData.sent_date || tender.feedback_survey?.sent_date,
      sent_to_email: feedbackData.sent_to_email || tender.feedback_survey?.sent_to_email,
      feedback_notes: feedbackData.feedback_notes || tender.feedback_survey?.feedback_notes
    });
  };

  return (
    <Card className="bg-[#2A3240] border-white/10 mt-6">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-white flex items-center gap-2 text-lg">
            <MessageSquare className="w-5 h-5 text-[#F5A623]" />
            Feedback Survey
          </CardTitle>
          <Badge className={`text-xs ${
            tender.feedback_survey?.status === 'Response Received' ? 'bg-green-500/20 text-green-400 border-green-500/30' :
            tender.feedback_survey?.status === 'Sent' ? 'bg-blue-500/20 text-blue-400 border-blue-500/30' :
            'bg-gray-500/20 text-gray-400 border-gray-500/30'
          }`}>
            {tender.feedback_survey?.status || 'Not Sent'}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-gray-400 text-sm">Track feedback requests for this lost tender</p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-gray-300 text-sm">Status</Label>
            <Select 
              value={feedbackData.status || tender.feedback_survey?.status || 'Not Sent'}
              onValueChange={(value) => setFeedbackData(prev => ({ ...prev, status: value }))}
              disabled={isArchived || !canEdit}
            >
              <SelectTrigger className="bg-[#1E2530] border-white/20 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#2A3240] border-white/10">
                {FEEDBACK_STATUS_OPTIONS.map(status => (
                  <SelectItem key={status} value={status} className="text-white">{status}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <Label className="text-gray-300 text-sm">Sent To Email</Label>
            <Input
              type="email"
              value={feedbackData.sent_to_email || tender.feedback_survey?.sent_to_email || tender.email || ''}
              onChange={(e) => setFeedbackData(prev => ({ ...prev, sent_to_email: e.target.value }))}
              className="bg-[#1E2530] border-white/20 text-white"
              disabled={isArchived || !canEdit}
              placeholder="client@example.com"
            />
          </div>
          
          <div className="space-y-2">
            <Label className="text-gray-300 text-sm">Date Sent</Label>
            <Input
              type="date"
              value={feedbackData.sent_date || tender.feedback_survey?.sent_date || ''}
              onChange={(e) => setFeedbackData(prev => ({ ...prev, sent_date: e.target.value }))}
              className="bg-[#1E2530] border-white/20 text-white"
              disabled={isArchived || !canEdit}
            />
          </div>
          
          <div className="space-y-2 md:col-span-2">
            <Label className="text-gray-300 text-sm">Feedback Notes</Label>
            <Textarea
              value={feedbackData.feedback_notes || tender.feedback_survey?.feedback_notes || ''}
              onChange={(e) => setFeedbackData(prev => ({ ...prev, feedback_notes: e.target.value }))}
              className="bg-[#1E2530] border-white/20 text-white resize-none"
              rows={3}
              disabled={isArchived || !canEdit}
              placeholder="Record any feedback received from the client..."
            />
          </div>
        </div>

        {!isArchived && canEdit && (
          <div className="flex gap-2 pt-2">
            <Button
              onClick={handleSave}
              disabled={submitting}
              className="bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D]"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save Feedback
            </Button>
            {(!tender.feedback_survey?.status || tender.feedback_survey?.status === 'Not Sent') && (
              <Button
                variant="outline"
                onClick={onMarkSent}
                disabled={submitting}
                className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
              >
                <Send className="w-4 h-4 mr-2" />
                Mark as Sent
              </Button>
            )}
          </div>
        )}

        {tender.feedback_survey?.sent_date && (
          <div className="pt-3 border-t border-white/10 text-sm text-gray-400">
            Survey sent on {format(parseISO(tender.feedback_survey.sent_date), 'dd MMM yyyy')}
            {tender.feedback_survey.sent_to_email && ` to ${tender.feedback_survey.sent_to_email}`}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
